
import { useState } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import {
  createManualPlayer,
  updateManualPlayer,
  deleteManualPlayer,
  checkEmailExists,
  getManualPlayers,
  mergeManualPlayer,
} from '@/integrations/supabase/manualPlayerService';
import { User, toManualUserId } from '@/types';
import { notifyFriendInvite } from '@/utils/notificationUtils';

/**
 * Hook for managing friendship actions (send, accept, reject, remove)
 */
export const useFriendshipActions = (refreshFriendships: () => Promise<void>) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const { user } = useAuth();

  const sendFriendRequest = async (friendId: string) => {
    if (!user) return null;
    setIsProcessing(true);

    try {
      const { data, error } = await supabase
        .from('friendships')
        .insert({
          user_id: user.id,
          friend_id: friendId,
          status: 'pending'
        })
        .select()
        .single();

      if (error) throw error;

      toast.success('Friend request sent');

      // Fire-and-forget: send email notification
      Promise.resolve(supabase
        .from('profiles').select('email, name').eq('id', friendId).single())
        .then(({ data: profile }) => {
          if (profile?.email) {
            notifyFriendInvite({
              type: 'friend_request',
              recipientEmail: profile.email,
              recipientName: profile.name || undefined,
            });
          }
        })
        .catch(() => {}); // Silently ignore notification failures

      await refreshFriendships();
      return data;
    } catch (error) {
      console.error('Error sending friend request:', error);

      if (typeof error === 'object' && error !== null && 'code' in error && error.code === '23505') {
        toast.error('Friend request already exists');
      } else {
        toast.error('Failed to send friend request');
      }

      return null;
    } finally {
      setIsProcessing(false);
    }
  };

  const acceptFriendRequest = async (friendshipId: string) => {
    setIsProcessing(true);
    try {
      // Get the friendship to find the other user's profile
      const { data: friendship, error: fetchError } = await supabase
        .from('friendships')
        .select('user_id, friend_id')
        .eq('id', friendshipId)
        .single();

      if (fetchError) throw fetchError;

      const { error } = await supabase
        .from('friendships')
        .update({ status: 'accepted' })
        .eq('id', friendshipId);

      if (error) throw error;

      toast.success('Friend request accepted');

      // Auto-merge: check if any manual players match the new friend's email
      if (friendship) {
        const newFriendId = friendship.user_id === user?.id ? friendship.friend_id : friendship.user_id;
        try {
          const { data: profile } = await supabase
            .from('profiles')
            .select('email, name')
            .eq('id', newFriendId)
            .single();

          if (profile?.email) {
            const manualPlayers = await getManualPlayers();
            const matchingPlayers = manualPlayers.filter(
              mp => mp.email?.toLowerCase() === profile.email?.toLowerCase()
            );

            for (const mp of matchingPlayers) {
              await mergeManualPlayer(mp.id, newFriendId);
              toast.success(`Merged manual player "${mp.name}" with their real account!`);
            }
          }
        } catch (mergeError) {
          console.error('Error during auto-merge check:', mergeError);
          // Don't fail the accept if merge check fails
        }
      }

      await refreshFriendships();
    } catch (error) {
      console.error('Error accepting friend request:', error);
      toast.error('Failed to accept friend request');
    } finally {
      setIsProcessing(false);
    }
  };

  const rejectFriendRequest = async (friendshipId: string) => {
    setIsProcessing(true);
    try {
      const { error } = await supabase
        .from('friendships')
        .delete()
        .eq('id', friendshipId);

      if (error) throw error;

      toast.success('Friend request rejected');
      await refreshFriendships();
    } catch (error) {
      console.error('Error rejecting friend request:', error);
      toast.error('Failed to reject friend request');
    } finally {
      setIsProcessing(false);
    }
  };

  const removeFriend = async (friendshipId: string) => {
    setIsProcessing(true);
    try {
      const { error } = await supabase
        .from('friendships')
        .delete()
        .eq('id', friendshipId);

      if (error) throw error;

      toast.success('Friend removed');
      await refreshFriendships();
    } catch (error) {
      console.error('Error removing friend:', error);
      toast.error('Failed to remove friend');
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Create a manual friend (player without an account).
   * If email matches an existing profile, sends a friend request instead.
   * Returns the created User object, or null if a friend request was sent instead.
   */
  const createManualFriend = async (
    name: string,
    email?: string
  ): Promise<{ user?: User; sentRequest?: boolean }> => {
    if (!user) return {};
    setIsProcessing(true);

    try {
      // If email provided, check if account exists
      if (email) {
        const { exists, profileId, name: profileName } = await checkEmailExists(email);
        if (exists && profileId) {
          // Auto-send friend request instead
          await sendFriendRequest(profileId);
          toast.success(`Found existing account for "${profileName || email}" — friend request sent!`);
          return { sentRequest: true };
        }
      }

      // Create manual player
      const { data: mp, error } = await createManualPlayer(name, email);
      if (error || !mp) {
        toast.error(error || 'Failed to create manual player');
        return {};
      }

      toast.success(`Added "${name}" as a manual player`);
      await refreshFriendships();

      return {
        user: {
          id: toManualUserId(mp.id),
          name: mp.name,
          email: mp.email,
          isManual: true,
          manualPlayerId: mp.id,
        },
      };
    } catch (error) {
      console.error('Error creating manual friend:', error);
      toast.error('Failed to add manual player');
      return {};
    } finally {
      setIsProcessing(false);
    }
  };

  const updateManualFriend = async (
    manualPlayerId: string,
    updates: { name?: string; email?: string }
  ) => {
    setIsProcessing(true);
    try {
      // If updating email, check if account exists
      if (updates.email) {
        const { exists, profileId } = await checkEmailExists(updates.email);
        if (exists && profileId) {
          toast.info('This email belongs to an existing account. Send them a friend request instead.');
          return { exists: true, profileId };
        }
      }

      const { error } = await updateManualPlayer(manualPlayerId, updates);
      if (error) {
        toast.error(error);
        return {};
      }

      toast.success('Player updated');
      await refreshFriendships();
      return {};
    } catch (error) {
      console.error('Error updating manual friend:', error);
      toast.error('Failed to update player');
      return {};
    } finally {
      setIsProcessing(false);
    }
  };

  const removeManualFriend = async (manualPlayerId: string) => {
    setIsProcessing(true);
    try {
      const { error } = await deleteManualPlayer(manualPlayerId);
      if (error) {
        toast.error(error);
        return;
      }

      toast.success('Manual player removed');
      await refreshFriendships();
    } catch (error) {
      console.error('Error removing manual friend:', error);
      toast.error('Failed to remove manual player');
    } finally {
      setIsProcessing(false);
    }
  };

  return {
    sendFriendRequest,
    acceptFriendRequest,
    rejectFriendRequest,
    removeFriend,
    createManualFriend,
    updateManualFriend,
    removeManualFriend,
    isProcessing
  };
};
